import React, { useEffect, useState } from "react"
import { Navigation } from "@kit/design/navigation"
import { TrialIndicator, TrialIndicatorPill } from "@kit/design/trial-indicator"
import { ProgressCircle } from "@kit/design/progress-circle"
import { CountBadge } from "@kit/design/count-badge"
import { useMediaBreakpoint } from "@kit/design/use-media-breakpoint"
import mount from "core/mount"
import { Link } from "core/link"
import { navName, groupName, trackNavClick } from "./utils/track-nav-click"
import { useCurrentUser } from "hooks/use-current-user"
import { useFeature } from "hooks/use-feature"
import { StatusBanner } from "./utils/status-banner"
import { UserAccountMenu } from "./user-account-menu"
import { BulkNotificationsDrawer } from "app/bulk-notifications"
import { useBulkNotifications } from "app/bulk-notifications/use-bulk-notifications"
import { DiscoveryHub } from "app/discovery-hub"

// Routes whose nav should always open collapsed, regardless of the user's
// saved preference (their manual toggles still persist for other pages).
// Add routes here as they adopt this treatment.
const COLLAPSE_NAV_PATHS = [
  /^\/campaigns\/\d+/, // broadcast pages (draft and share editors)
  /^\/landing-pages\/\d+\/pages\/\d+\/edit/, // landing page editor
  /^\/forms\/designers\/\d+\/edit/, // form designer
  /^\/forms\/\d+\/reports/, // form reports
  /^\/creator-network\/customize/, // creator network customization
  /^\/newsletter-site$/, // newsletter site editor
  /^\/newsletter-site\/reports/, // newsletter site reports
  /^\/products\/\d+\/edit/, // product editor
  /^\/products\/\d+\/reports/, // product reports
  /^\/tips\/\d+\/edit/, // tip editor
  /^\/automations\/\d+\/edit/, // visual automation editor
  /^\/sequences\/\d+/, // sequence pages (editor, reports etc.)
  /^\/feeds\/\d+/, // RSS feed pages (steps, reports etc.)
  /^\/account\/email_layout_templates\/new/, // new HTML email template editor
  /^\/account\/email_layout_templates\/\d+\/edit/ // edit HTML email template editor
]

function NavigationFooter({
  user,
  onUpgrade,
  renderLink,
  trackClick,
  onWhatsNewClick,
  whatsNewCount,
  notificationsLeading,
  unseenCount,
  onNotificationsClick
}) {
  return (
    <Navigation.Footer>
      {user.onTrial && (
        <Navigation.FooterCallout
          collapsed={
            <TrialIndicatorPill
              daysRemaining={user.trialDaysRemaining ?? 0}
              daysTotal={user.trialDaysTotal ?? 0}
              onUpgrade={onUpgrade}
            />
          }
        >
          <TrialIndicator
            daysRemaining={user.trialDaysRemaining ?? 0}
            daysTotal={user.trialDaysTotal ?? 0}
            onUpgrade={onUpgrade}
          />
        </Navigation.FooterCallout>
      )}
      <Navigation.Menu>
        {notificationsLeading && (
          <Navigation.Item
            leading={notificationsLeading}
            label="Notifications"
            trailing={
              unseenCount > 0 ? <CountBadge count={unseenCount} /> : null
            }
            render={<button type="button" onClick={onNotificationsClick} />}
          />
        )}
        <Navigation.Item
          leading="settings-cog"
          label="Settings"
          pathPrefix="/account_settings"
          render={renderLink(
            "/account_settings/account_info",
            trackClick("Settings")
          )}
        />
      </Navigation.Menu>
      <UserAccountMenu
        user={user}
        onWhatsNewClick={onWhatsNewClick}
        whatsNewCount={whatsNewCount}
      />
    </Navigation.Footer>
  )
}

export function AppNavigation({
  currentPath: currentPathProp,
  renderLink = (href, onClick) => <Link href={href} onClick={onClick} />,
  onCollapsedChange,
  mobile: mobileProp
} = {}) {
  const user = useCurrentUser()
  const { enabled: webhooksV2UiEnabled } = useFeature.static("webhooks_v2_ui")
  const currentPath = currentPathProp ?? window.location.pathname
  // Fallback for standalone ERB mounts (application.html.erb) where no mobile prop is passed.
  // In VerticalNavLayout, isMobile is derived once and passed down to keep a single source of truth.
  const isMobileFromBreakpoint = !useMediaBreakpoint("md")
  const isMobile =
    mobileProp !== undefined ? mobileProp : isMobileFromBreakpoint
  // Tracking rides on the link element as an onClick passed through renderLink,
  // so the click event fires before navigation. renderLink owns which link
  // component to render; this stays out of it and only adds our analytics.
  const trackClick =
    (label, section, location = "vertical_nav") =>
    () =>
      trackNavClick({
        name: navName(label),
        text: label,
        section,
        location,
        isMobile
      })
  const nav = (path, label, section) => ({
    pathPrefix: path,
    render: renderLink(path, trackClick(label, section))
  })
  // The mobile-only dock surfaces the same destinations as the sidebar, so tag
  // its clicks with a distinct location to tell dock usage apart in Mixpanel.
  const dockNav = (path, label) => ({
    pathPrefix: path,
    render: renderLink(path, trackClick(label, undefined, "dock_navigation"))
  })
  // Only manual opens are tracked: the DS auto-opens a group for the active
  // route programmatically (without firing onOpenChange), so an event here
  // always means the user deliberately expanded the group.
  const trackGroupOpen = label => open => {
    if (open) {
      trackNavClick({
        name: groupName(label),
        text: label,
        elementType: "button",
        location: "vertical_nav",
        isMobile
      })
    }
  }
  const [discoveryHubOpen, setDiscoveryHubOpen] = useState(false)
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [whatsNewCount, setWhatsNewCount] = useState(0)
  const { topics, activeTopic, progress, unseenCount, hasTopics, markSeen } =
    useBulkNotifications()
  const notificationsLeading = hasTopics ? (
    activeTopic ? (
      <ProgressCircle progress={progress} />
    ) : (
      "alarm"
    )
  ) : null

  const handleNotificationsOpen = () => {
    trackNavClick({
      name: "notifications",
      text: "Notifications",
      elementType: "button",
      location: "vertical_nav",
      isMobile
    })
    setNotificationsOpen(true)
    markSeen()
  }
  const startCollapsed =
    isMobile || COLLAPSE_NAV_PATHS.some(re => re.test(currentPath))
  // In the standalone ERB mount there is no onCollapsedChange consumer, so we
  // handle inert on the sibling <main> directly. The Inertia path provides
  // onCollapsedChange and manages inert via mobileNavExpanded in AppLayout.Content.
  const handleCollapsedChange = collapsed => {
    if (!onCollapsedChange && isMobile) {
      document
        .querySelector("#main-content")
        ?.toggleAttribute("inert", !collapsed)
    }
    onCollapsedChange?.(collapsed)
  }
  // startCollapsed force-collapses the nav on resize to desktop without firing
  // onCollapsedChange, so inert set during mobile is never removed. Clear it here.
  useEffect(() => {
    if (!onCollapsedChange && !isMobile) {
      document.querySelector("#main-content")?.removeAttribute("inert")
    }
  }, [isMobile, onCollapsedChange])
  const isPaidPlan =
    user.access_level?.pro ||
    user.access_level?.standard ||
    user.access_level?.newsletter_paid
  const accountType = isPaidPlan && !user.onTrial ? "paid" : "free"
  return (
    <Navigation
      currentPath={currentPath}
      startCollapsed={startCollapsed}
      mobile={isMobile}
      accountType={accountType}
      onCollapsedChange={handleCollapsedChange}
    >
      <Navigation.Sidebar>
        <Navigation.Header>
          <Navigation.HeaderCollapsibleContent>
            <Navigation.LogoLink {...nav("/dashboard", "Logo")} />
            <StatusBanner />
          </Navigation.HeaderCollapsibleContent>
          <Navigation.Trigger />
        </Navigation.Header>

        <Navigation.Menu>
          <Navigation.Item
            leading="home"
            label="Dashboard"
            {...nav("/dashboard", "Dashboard")}
          />
          <Navigation.Item
            leading="three-people"
            label="Subscribers"
            tooltip="Browse all your subscribers and customers"
            {...nav("/subscribers", "Subscribers")}
          />
          <Navigation.Item
            leading="megaphone"
            label="Broadcasts"
            tooltip="Send an email to your audience and post to your public feed"
            {...nav("/campaigns", "Broadcasts")}
          />

          <Navigation.Group
            icon="trending-up"
            label="Audience growth"
            onOpenChange={trackGroupOpen("Audience growth")}
          >
            <Navigation.GroupItem
              label="Landing Pages & Forms"
              tooltip="Grow your list with a hosted landing page or embedded form"
              {...nav("/forms", "Landing Pages & Forms", "Audience growth")}
            />
            <Navigation.GroupItem
              label="Recommendations"
              tooltip="Grow your list by cross-promoting with other creators in the Creator Network"
              {...nav("/creator-network", "Recommendations", "Audience growth")}
            />
            <Navigation.GroupItem
              label="Newsletter Site"
              tooltip="Your personal dedicated page for subscribers to find your content and products"
              {...nav("/newsletter-site", "Newsletter Site", "Audience growth")}
            />
          </Navigation.Group>

          <Navigation.Group
            icon="money"
            label="Monetization"
            onOpenChange={trackGroupOpen("Monetization")}
          >
            <Navigation.GroupItem
              label="Products"
              tooltip="Sell digital products and content subscriptions"
              {...nav("/products", "Products", "Monetization")}
            />
            <Navigation.GroupItem
              label="Sponsorships"
              badge="paid"
              tooltip="Monetize your content with sponsorships and advertising"
              {...nav("/ads", "Sponsorships", "Monetization")}
            />
            {user.canUsePaidRecommendations && (
              <Navigation.GroupItem
                label="Paid Recommendations"
                badge="paid"
                tooltip="Get paid to recommend other creators in the Creator Network"
                {...nav(
                  "/paid_recommendations",
                  "Paid Recommendations",
                  "Monetization"
                )}
              />
            )}
            <Navigation.GroupItem
              label="Tips"
              tooltip="Give your audience a simple way to support your work"
              {...nav("/tips", "Tips", "Monetization")}
            />
            <Navigation.GroupItem
              label="Payouts"
              tooltip="See a breakdown of payouts you've received"
              {...nav("/account_settings/commerce", "Payouts", "Monetization")}
            />
          </Navigation.Group>

          <Navigation.Group
            icon="bolt"
            label="Automation"
            badge="paid"
            onOpenChange={trackGroupOpen("Automation")}
          >
            <Navigation.GroupItem
              label="Visual Automations"
              tooltip="Create automated funnels that nurture and sell 24/7"
              {...nav("/automations", "Visual Automations", "Automation")}
            />
            <Navigation.GroupItem
              label="Rules"
              tooltip={'Simple "if this, then that" automation rules'}
              {...nav("/rules", "Rules", "Automation")}
            />
            <Navigation.GroupItem
              label="Email Sequences"
              tooltip="Send a series of emails in order"
              {...nav("/sequences", "Email Sequences", "Automation")}
            />
            {webhooksV2UiEnabled && (
              <Navigation.GroupItem
                label="Webhooks"
                tooltip="Send real-time event notifications to your apps and tools"
                {...nav("/webhooks")}
              />
            )}
            <Navigation.GroupItem
              label="Kit MCP"
              badge="beta"
              tooltip="Connect your Kit account to your favorite AI tool"
              {...nav("/kit-mcp", "Kit MCP", "Automation")}
            />
            <Navigation.GroupItem
              label="RSS"
              tooltip="Send emails or digests automatically when your RSS feed updates"
              {...nav("/feeds", "RSS", "Automation")}
            />
          </Navigation.Group>

          <Navigation.Item
            leading="app-store"
            label="Apps"
            badge="paid"
            tooltip="Connect your favorite creator tools to Kit"
            {...nav("/apps", "Apps")}
          />
        </Navigation.Menu>

        <NavigationFooter
          user={user}
          onUpgrade={() => {
            trackNavClick({
              name: "upgrade",
              text: "Upgrade",
              elementType: "button",
              location: "vertical_nav",
              isMobile
            })
            window.location.href = "/upgrade"
          }}
          renderLink={renderLink}
          trackClick={trackClick}
          onWhatsNewClick={() => setDiscoveryHubOpen(true)}
          whatsNewCount={whatsNewCount}
          notificationsLeading={notificationsLeading}
          unseenCount={unseenCount}
          onNotificationsClick={handleNotificationsOpen}
        />
      </Navigation.Sidebar>

      <Navigation.Dock>
        <Navigation.Dock.Trigger />
        <Navigation.Dock.Actions>
          <Navigation.Dock.Action
            label="Dashboard"
            icon="home"
            {...dockNav("/dashboard", "Dashboard")}
          />
          <Navigation.Dock.Action
            label="Subscribers"
            icon="three-people"
            {...dockNav("/subscribers", "Subscribers")}
          />
          <Navigation.Dock.Action
            label="Broadcasts"
            icon="megaphone"
            {...dockNav("/campaigns", "Broadcasts")}
          />
        </Navigation.Dock.Actions>
      </Navigation.Dock>

      <BulkNotificationsDrawer
        open={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
        topics={topics}
      />

      <DiscoveryHub
        open={discoveryHubOpen}
        onClose={() => setDiscoveryHubOpen(false)}
        onNotificationCount={setWhatsNewCount}
      />
    </Navigation>
  )
}

mount({ AppNavigation })
